import { Component, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "./button";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: (error: Error | null, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack?: string | null }) {
    console.error("[ErrorBoundary]", error, errorInfo);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback(this.state.error, this.reset);
    }
    return this.props.children;
  }
}

interface AppErrorFallbackProps {
  error: Error | null;
}

export function AppErrorFallback({ error }: AppErrorFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-8 max-w-2xl mx-auto text-center">
      <h1 className="text-2xl font-semibold">Une erreur est survenue</h1>
      {error?.message && (
        <p className="text-sm text-muted-foreground break-words">{error.message}</p>
      )}
      <div className="flex gap-3 mt-2">
        <Button variant="default" onClick={() => window.location.reload()}>
          Réessayer
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            window.location.href = "/";
          }}
        >
          Retour a l'accueil
        </Button>
      </div>
    </div>
  );
}
